import { Minus, Plus, Undo2 } from "lucide-react";

import type { PointerEvent, RefObject } from "react";
import { useCallback, useEffect, useRef, useState } from "react";

const DEFAULT_DRAWING_COLOR = "#ff3b30";
const DRAWING_COLORS = [
  DEFAULT_DRAWING_COLOR,
  "#8F14E5",
  "#2563eb",
  "#16a34a",
  "#ffffff",
];

type SmileSimulationDrawingLayerProps = {
  isEnabled: boolean;
  isDrawingMode: boolean;
  pan: { x: number; y: number };
  targetRef: RefObject<HTMLDivElement | null>;
  zoom: number;
};

export function SmileSimulationDrawingLayer({
  isEnabled,
  isDrawingMode,
  pan,
  targetRef,
  zoom,
}: SmileSimulationDrawingLayerProps) {
  const canvasElementRef = useRef<HTMLCanvasElement | null>(null);
  const historyRef = useRef<ImageData[]>([]);
  const isDrawingRef = useRef(false);
  const lastSizeRef = useRef({ height: 0, width: 0 });
  const [strokeColor, setStrokeColor] = useState(DEFAULT_DRAWING_COLOR);
  const [strokeWidth, setStrokeWidth] = useState(3);
  const [drawingCount, setDrawingCount] = useState(0);
  const iconButtonClass =
    "flex h-9 w-9 items-center justify-center rounded-full bg-white/90 text-[#2b2b2b] transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-45";

  useEffect(() => {
    const target = targetRef.current;
    const canvas = canvasElementRef.current;

    if (!target || !canvas || !isEnabled) {
      return;
    }

    const syncCanvasSize = () => {
      const width = target.clientWidth;
      const height = target.clientHeight;

      if (!width || !height) {
        return;
      }

      const previousSize = lastSizeRef.current;
      const previousCanvas = document.createElement("canvas");
      previousCanvas.width = Math.max(1, previousSize.width);
      previousCanvas.height = Math.max(1, previousSize.height);
      const previousContext = previousCanvas.getContext("2d");
      const context = canvas.getContext("2d");

      if (!context || !previousContext) {
        return;
      }

      if (previousSize.width && previousSize.height) {
        previousContext.drawImage(canvas, 0, 0);
      }

      canvas.width = width;
      canvas.height = height;

      if (previousSize.width && previousSize.height) {
        context.drawImage(previousCanvas, 0, 0, width, height);
      }

      historyRef.current = [];
      setDrawingCount(0);
      lastSizeRef.current = { height, width };
    };

    syncCanvasSize();

    const resizeObserver = new ResizeObserver(syncCanvasSize);
    resizeObserver.observe(target);

    return () => {
      resizeObserver.disconnect();
    };
  }, [isEnabled, targetRef]);

  const getCanvasPoint = useCallback((event: PointerEvent) => {
    const canvas = canvasElementRef.current;

    if (!canvas) {
      return null;
    }

    const rect = canvas.getBoundingClientRect();

    return {
      x: ((event.clientX - rect.left) / rect.width) * canvas.width,
      y: ((event.clientY - rect.top) / rect.height) * canvas.height,
    };
  }, []);

  if (!isEnabled) {
    return null;
  }

  const handleUndo = () => {
    const canvas = canvasElementRef.current;
    const context = canvas?.getContext("2d");
    const previousImage = historyRef.current.pop();

    if (!canvas || !context || !previousImage) {
      return;
    }

    context.clearRect(0, 0, canvas.width, canvas.height);
    context.putImageData(previousImage, 0, 0);
    setDrawingCount(historyRef.current.length);
  };

  const updateStrokeWidth = (nextStrokeWidth: number) => {
    setStrokeWidth(Math.min(12, Math.max(1, nextStrokeWidth)));
  };

  const handlePointerDown = (event: PointerEvent<HTMLCanvasElement>) => {
    if (!isDrawingMode) {
      return;
    }

    const canvas = canvasElementRef.current;
    const context = canvas?.getContext("2d");
    const point = getCanvasPoint(event);

    if (!canvas || !context || !point) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    canvas.setPointerCapture(event.pointerId);
    historyRef.current.push(
      context.getImageData(0, 0, canvas.width, canvas.height),
    );
    isDrawingRef.current = true;
    context.beginPath();
    context.moveTo(point.x, point.y);
  };

  const handlePointerMove = (event: PointerEvent<HTMLCanvasElement>) => {
    if (!isDrawingMode || !isDrawingRef.current) {
      return;
    }

    const context = canvasElementRef.current?.getContext("2d");
    const point = getCanvasPoint(event);

    if (!context || !point) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    context.lineCap = "round";
    context.lineJoin = "round";
    context.strokeStyle = strokeColor;
    context.lineWidth = strokeWidth;
    context.lineTo(point.x, point.y);
    context.stroke();
  };

  const handlePointerUp = (event: PointerEvent<HTMLCanvasElement>) => {
    if (!isDrawingRef.current) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    isDrawingRef.current = false;
    setDrawingCount(historyRef.current.length);
  };

  return (
    <>
      <canvas
        ref={canvasElementRef}
        className={`absolute inset-0 z-20 h-full w-full origin-center ${
          isDrawingMode ? "pointer-events-auto" : "pointer-events-none"
        }`}
        onPointerCancel={handlePointerUp}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        style={{
          transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
        }}
      />

      <div
        className="absolute inset-x-4 bottom-4 z-30 flex flex-wrap items-center justify-center gap-2 rounded-[1rem] bg-black/55 p-2 text-xs font-semibold text-white backdrop-blur"
        onPointerDown={(event) => event.stopPropagation()}
        onPointerMove={(event) => event.stopPropagation()}
        onPointerUp={(event) => event.stopPropagation()}
      >
        <div className="flex items-center gap-1">
          {DRAWING_COLORS.map((color) => (
            <button
              key={color}
              type="button"
              aria-label={`Select ${color}`}
              onClick={() => setStrokeColor(color)}
              className={`h-6 w-6 rounded-full border-2 ${
                strokeColor === color ? "border-white" : "border-transparent"
              }`}
              style={{ backgroundColor: color }}
            />
          ))}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label="Decrease stroke width"
            onClick={() => updateStrokeWidth(strokeWidth - 1)}
            disabled={strokeWidth <= 1}
            className={iconButtonClass}
          >
            <Minus className="h-4 w-4" />
          </button>
          <input
            aria-label="Stroke width"
            type="range"
            min={1}
            max={12}
            step={0.5}
            value={strokeWidth}
            onChange={(event) => updateStrokeWidth(Number(event.target.value))}
            className="w-20 accent-[#8F14E5]"
          />
          <button
            type="button"
            aria-label="Increase stroke width"
            onClick={() => updateStrokeWidth(strokeWidth + 1)}
            disabled={strokeWidth >= 12}
            className={iconButtonClass}
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>
        <button
          type="button"
          aria-label="Undo"
          onClick={handleUndo}
          disabled={!drawingCount}
          className={iconButtonClass}
        >
          <Undo2 className="h-4 w-4" />
        </button>
      </div>
    </>
  );
}
